// Copyright (c) Microgreen Manager LLC. All rights reserved. Proprietary and confidential. Unauthorized copying is strictly prohibited.
import React from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useLoggedInState } from '../../context/LoggedInContext';
import * as routes from '../../routes';
import { Button, Menu, MenuItem, Typography } from '../base';
import { LinkUnderline } from '../link';

export interface TabType {
  id: string;
  label: string;
  selected: boolean;
  onClick?: () => void;
  href: string;
  requireSignIn?: boolean;
  groupId?: string;
}

interface Props {
  tabs: Array<TabType>;
}

const AppLinkUnderline = ({
  children,
  isActive,
  onClick,
  onEnter,
}: {
  children: React.ReactNode;
  isActive: boolean;
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
  onEnter: (e: React.KeyboardEvent<HTMLButtonElement>) => void;
}) => (
  <Button
    onClick={onClick}
    onKeyDown={onEnter}
    tabIndex={0}
    disableRipple
    sx={{
      textTransform: 'none',
      color: 'primary.contrastText',
      py: 0,
      px: 2,
      textDecoration: 'none',
      border: 'none',
      display: 'inline-block',
      '&::after': {
        borderBottom: '1px solid white',
        content: '""',
        display: 'block',
        transform: isActive ? 'scaleX(1)' : 'scaleX(0)',
        transition: 'transform 0.2s ease-in-out',
      },
      '&:hover::after': {
        transform: 'scaleX(1)',
      },
      whiteSpace: 'nowrap',
      cursor: 'pointer',
    }}
  >
    <Typography>{children}</Typography>
  </Button>
);

export function Tabs({ tabs }: Props): React.ReactElement {
  const { isLoggedIn } = useLoggedInState();
  const navigate = useNavigate();
  const location = useLocation();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [dropdownAnchorEl, setDropdownAnchorEl] = React.useState<null | HTMLElement>(null);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>, tabId?: string) => {
    if (tabId) {
      setDropdownAnchorEl(event.currentTarget);
    } else {
      if (!isLoggedIn) {
        navigate(routes.HOME);
      } else {
        setAnchorEl(event.currentTarget);
      }
    }
  };
  const handleEnter = (event: React.KeyboardEvent<HTMLElement>, tabId?: string) => {
    if (event.key === 'Enter') {
      if (tabId) {
        setDropdownAnchorEl(event.currentTarget);
      } else {
        if (!isLoggedIn) {
          navigate(routes.HOME);
        } else {
          setAnchorEl(event.currentTarget);
        }
      }
    }
  };
  const handleMenuItemKeyDown = (key: string, href: string) => {
    if (key === 'Enter' || key === ' ') {
      navigate(href);
      handleClose();
    }
  };
  const handleClose = () => {
    setAnchorEl(null);
    setDropdownAnchorEl(null);
  };

  const freeToolsLink = (
    <AppLinkUnderline
      key="free-tools-link"
      onClick={(e) => handleClick(e, 'free-tools')}
      onEnter={(e) => handleEnter(e, 'free-tools')}
      isActive={routes.isFreeTool(location.pathname)}
    >
      Free Tools
    </AppLinkUnderline>
  );

  const freeToolsMenu = (
    <Menu
      id="dropdown-menu-free-tools"
      anchorEl={dropdownAnchorEl}
      keepMounted
      open={Boolean(dropdownAnchorEl)}
      onClose={handleClose}
    >
      {tabs
        .filter((tab) => tab.groupId === 'free-tools')
        .map((tab) => (
          <MenuItem
            key={tab.id}
            onClick={() => {
              handleClose();
              navigate(tab.href);
            }}
            onKeyDown={(e) => handleMenuItemKeyDown(e.key, tab.href)}
            selected={tab.selected}
          >
            {tab.label}
          </MenuItem>
        ))}
    </Menu>
  );

  const appButton = (
    <AppLinkUnderline
      onClick={handleClick}
      onEnter={handleEnter}
      key="app"
      isActive={routes.requiresAuth(location.pathname)}
    >
      App
    </AppLinkUnderline>
  );
  const menu = (
    <Menu id="simple-menu-up" anchorEl={anchorEl} keepMounted open={Boolean(anchorEl)} onClose={handleClose}>
      {tabs
        .filter((tab) => tab.requireSignIn)
        .map((button) => {
          const child = (
            <MenuItem
              key={button.id}
              onClick={() => {
                handleClose();
                navigate(button.href);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleClose();
                  navigate(button.href);
                }
              }}
              selected={button.selected}
            >
              {button.label}
            </MenuItem>
          );
          return child;
        })}
    </Menu>
  );

  const tabsOut = tabs
    .filter((tab) => !tab.requireSignIn)
    .filter((tab) => tab.groupId === undefined)
    .map((tab) => {
      const child = (
        <LinkUnderline
          sx={{ color: 'primary.contrastText', py: 0, px: 2 }}
          key={tab.id}
          to={tab.href}
          isActive={tab.href === location.pathname}
        >
          <Typography variant="body1">{tab.label}</Typography>
        </LinkUnderline>
      );
      return child;
    });
  tabsOut.push(freeToolsLink);
  if (isLoggedIn) {
    tabsOut.push(appButton);
  }

  return (
    <>
      {tabsOut}
      {menu}
      {freeToolsMenu}
    </>
  );
}
